import { keyframes } from '@emotion/css'
import styled, { CSSObject } from '@emotion/styled'
import { tokens } from '@news-mono/design-tokens'
import { breakpoint, themedValue } from '../../..'
import { Sharing } from '../../../buttons/Sharing/Sharing'
import { colors } from '../../../__styling/settings/colors'
import { calcRem } from '../../../__styling/style-functions/calc-rem'

const gradialBlue = tokens['thewest'].colors.palette.gradialBlue
const gradialRed = tokens['sevennews'].colors.palette.gradialRed
const gradialPink = tokens['perthnow'].colors.palette.fadedPinkThulite

interface Keyframes {
    [key: string]: CSSObject
}

const shadingAnimation = (
    initialColor: string,
    finalColor: string,
): Keyframes => {
    return {
        '10%': {
            background: initialColor,
        },
        '100%': {
            background: finalColor,
        },
    }
}

export const StyledLiveEventPost = styled('div')<{
    /** Post has no bottom margin or border. Generally due to being placed in truncated container */
    noBotMargin?: boolean
    isActivePost?: boolean
}>(({ theme, noBotMargin, isActivePost }) => [
    {
        padding: theme.margins.md,
        marginBottom: noBotMargin ? undefined : theme.margins.sm,
        background: theme.colors.palette.white,
        boxShadow: noBotMargin
            ? undefined
            : themedValue(theme, {
                  thewest: `0px 2px 3px rgba(0, 0, 0, 0.08)`,
                  perthnow: `0px 2px 3px rgba(0, 0, 0, 0.08)`,
                  sevennews: `0px 0px 11px rgba(0, 0, 0, 0.25)`,
                  fallback: 'none',
              }),

        [breakpoint('sm')]: {
            margin: theme.margins.md,
            marginBottom: noBotMargin ? 0 : theme.margins.md,
        },
        [breakpoint('md')]: {
            padding: themedValue(theme, {
                thewest: calcRem(theme.margins.lg, theme.margins.md),
                fallback: theme.margins.md,
            }),
            margin: themedValue(theme, {
                thewest: 0,
                perthnow: 0,
                sevennews: calcRem(theme.margins.md, theme.margins.sm),
                fallback: undefined,
            }),
            marginBottom: noBotMargin
                ? 0
                : themedValue(theme, {
                      thewest: 0,
                      perthnow: 0,
                      sevennews: calcRem(theme.margins.md, theme.margins.sm),
                      fallback: undefined,
                  }),

            borderBottom: noBotMargin
                ? undefined
                : themedValue(theme, {
                      thewest: `1px solid ${colors.thewest.greyQuokka}`,
                      perthnow: `1px solid ${colors.thewest.greyQuokka}`,
                      sevennews: 'none',
                      fallback: undefined,
                  }),
        },
    },
    isActivePost && {
        animation: `${keyframes(
            shadingAnimation(
                themedValue(theme, {
                    thewest: gradialBlue,
                    sevennews: gradialRed,
                    perthnow: gradialPink,
                    fallback: gradialBlue,
                }),
                theme.colors.palette.white,
            ),
        )} 3s ease`,
    },
])

export const StyledLiveEventTitle = styled('h2')(({ theme }) => ({
    margin: themedValue(theme, {
        thewest: calcRem(theme.margins.md, 0),
        sevennews: calcRem(theme.margins.sm, 0),
        fallback: '',
    }),
    fontSize: themedValue(theme, {
        thewest: calcRem(26),
        perthnow: calcRem(26),
        sevennews: calcRem(21),
        fallback: undefined,
    }),
    fontFamily: themedValue(theme, {
        thewest: 'inherit',
        perthnow: 'inherit',
        sevennews: theme.fonts.sansSerif,
        fallback: undefined,
    }),
    fontWeight: themedValue(theme, {
        sevennews: 700,
        fallback: 400,
    }),
    color: theme.colors.palette.black,

    lineHeight: calcRem(31),
}))

export const StyledLiveEventArticleBlockContent = styled('div')(({ theme }) => [
    {
        margin: themedValue(theme, {
            thewest: calcRem(theme.margins.md, 0),
            perthnow: calcRem(theme.margins.md, 0),
            sevennews: calcRem(theme.margins.md, 0),
            fallback: undefined,
        }),
        '& > div > p': {
            fontFamily: themedValue(theme, {
                thewest: 'inherit',
                perthnow: 'inherit',
                sevennews: theme.fonts.sansSerif,
                fallback: undefined,
            }),
            fontSize: themedValue(theme, {
                thewest: calcRem(16),
                perthnow: calcRem(16),
                sevennews: calcRem(16),
                fallback: undefined,
            }),
            fontWeight: themedValue(theme, {
                sevennews: 400,
                fallback: 'inherit',
            }),
            lineHeight: themedValue(theme, {
                sevennews: calcRem(27),
                fallback: calcRem(24),
            }),
        },

        '& > div > blockquote > p': {
            marginLeft: themedValue(theme, {
                sevennews: 0,
                fallback: theme.margins.md,
            }),
            fontSize: themedValue(theme, {
                thewest: calcRem(21),
                perthnow: calcRem(21),
                sevennews: calcRem(18),
                fallback: undefined,
            }),
            fontWeight: themedValue(theme, {
                thewest: 400,
                perthnow: 400,
                sevennews: 700,
                fallback: undefined,
            }),
            fontFamily: themedValue(theme, {
                sevennews: theme.fonts.sansSerif,
                fallback: theme.fonts.serif,
            }),

            color: themedValue(theme, {
                sevennews: theme.colors.brand,
                fallback: 'inherit',
            }),
            lineHeight: calcRem(25),
        },
        '& > div > blockquote > div': {
            width: 4,
        },
    },
    theme.kind === 'sevennews' && {
        '& > div > blockquote ': {
            transform: 'translateX(0)',
            marginLeft: 0,
            marginRight: 0,
            width: '100%',
            maxWidth: '100%',
            '& > p': {
                maxWidth: '100%',
            },
        },
    },
])

export const StyledLiveEventSocialIcons = styled(Sharing)(({ theme }) => [
    {
        display: 'inline-block', // To avoid margin collapse
        '&': {
            marginBottom: 0,
            marginTop: themedValue(theme, {
                thewest: calcRem(theme.margins.sm),
                fallback: 0,
            }),
        },
        '& > button ': {
            position: 'relative',
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: 'inherit',
                fallback: undefined,
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyErmine,
                perthnow: colors.thewest.greyErmine,
                sevennews: 'inherit',
                fallback: undefined,
            }),
            border: themedValue(theme, {
                thewest: 'none',
                perthnow: 'none',
                sevennews: 'solid 1px',
                fallback: undefined,
            }),
            width: themedValue(theme, {
                thewest: calcRem(32),
                perthnow: calcRem(32),
                sevennews: calcRem(40),
                fallback: undefined,
            }),
            height: themedValue(theme, {
                thewest: calcRem(32),
                perthnow: calcRem(32),
                sevennews: calcRem(40),
                fallback: undefined,
            }),

            [breakpoint('md')]: {
                width: themedValue(theme, {
                    thewest: calcRem(24),
                    perthnow: calcRem(24),
                    sevennews: calcRem(40),
                    fallback: undefined,
                }),
                height: themedValue(theme, {
                    thewest: calcRem(24),
                    perthnow: calcRem(24),
                    sevennews: calcRem(40),
                    fallback: undefined,
                }),
            },
        },
        '& > button:hover ': {
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: theme.colors.brand,
                fallback: undefined,
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyDolphin,
                perthnow: colors.thewest.greyElephant,
                sevennews: 'none',
                fallback: undefined,
            }),
            border: themedValue(theme, {
                thewest: 'none',
                perthnow: 'none',
                sevennews: `solid ${theme.colors.brand} 1px`,
                fallback: undefined,
            }),
        },
        '& > button:active ': {
            color: themedValue(theme, {
                thewest: colors.thewest.greyKoala,
                perthnow: colors.thewest.greyKoala,
                sevennews: 'inherit',
                fallback: undefined,
            }),
            backgroundColor: themedValue(theme, {
                thewest: colors.thewest.greyQuokka,
                perthnow: colors.thewest.greyQuokka,
                sevennews: 'inherit',
                fallback: undefined,
            }),
            border: 'none',
        },
        '& > button.gtm-clipboard-share-button:active:before': {
            opacity: 1,
            transition: '0s',
        },
        '& > button.gtm-clipboard-share-button:before': {
            position: 'absolute',
            top: calcRem(-35),
            left: calcRem(-31),
            width: calcRem(98),
            opacity: 0,
            padding: calcRem(6),
            content: '"✓ Link Copied "',
            transition: 'opacity 1.7s cubic-bezier(.0,.05,1,-0.24)',
            fontFamily: theme.fonts.sansSerif,
            fontSize: themedValue(theme, {
                thewest: calcRem(13),
                perthnow: calcRem(13),
                sevennews: calcRem(12),
                fallback: undefined,
            }),
            fontWeight: 600,
            background: theme.colors.palette.black,
            color: theme.colors.palette.white,
        },
    },
    theme.kind === 'sevennews' && {
        '&::before': {
            display: 'none',
        },
        '& > button.gtm-facebook-share-button > svg': {
            top: '53%',
        },
    },
])
